import NotFound from '../NotFound';
import { CONTENT_PACK } from '../../data/contentPack';
import PackArticle from '../../components/seo/PackArticle';
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Globe, AlertTriangle, CheckCircle, Clock, FileText, ExternalLink, Shield } from 'lucide-react';
import { SEOLayout, AlertBox, StatCard } from '../../components/SEOLayout';
import { allCountriesData } from '../../data/seoDataMerged';
import { Button } from '../../components/ui/button';

const DestinationDetail = () => {
  const { slug } = useParams();
  const packPage = CONTENT_PACK[`/destinations/${slug}`];
  const country = allCountriesData[slug];

  if (packPage) return <PackArticle page={packPage} />;
  if (!country) return <NotFound />;

  const rules = country.importRules || {};

  return (
    <SEOLayout
      title={country.seoTitle || `${country.name} Pet Import Rules 2026`}
      description={country.seoDescription || `Complete guide to importing pets to ${country.name}. Quarantine rules, vaccination requirements, documentation needed.`}
      keywords={country.seoKeywords || [`${country.name} pet import`, `bring dog to ${country.name}`, `${country.name} pet rules`]}
      breadcrumbs={[
        { label: 'Destinations', href: '/destinations' },
        { label: country.name }
      ]}
    >
      {/* Hero */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <span className="text-5xl">{country.flag}</span>
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900">
              {country.name} Pet Import Requirements
            </h1>
            <p className="text-slate-600">
              {country.code} - Complete 2026 Guide
            </p>
          </div>
        </div>
        <p className="text-lg text-slate-700 max-w-3xl">
          Everything you need to know about importing your pet to {country.name}. 
          Vaccination requirements, documentation, quarantine rules, and more.
        </p>
      </div>

      {/* Warning Banner for Strict Countries */}
      {rules.quarantine && (
        <AlertBox type="danger" title={`Quarantine Required in ${country.name}`}>
          <p>
            {country.name} requires a mandatory quarantine period of <strong>{rules.quarantineDays}</strong> for imported pets.
            {rules.quarantineFacility && ` Pets must stay at ${rules.quarantineFacility}.`}
            Plan your travel well in advance to accommodate this requirement.
          </p>
        </AlertBox>
      )}

      {/* Quick Stats */}
      <div className="grid md:grid-cols-4 gap-4 my-8">
        <StatCard 
          value={rules.quarantine ? 'Yes' : 'No'} 
          label="Quarantine Required"
          type={rules.quarantine ? 'danger' : 'success'}
        />
        <StatCard 
          value={rules.rabiesWaitingPeriod || '21 days'} 
          label="Rabies Wait Period"
        />
        <StatCard 
          value={rules.importPermit ? 'Required' : 'Not Needed'} 
          label="Import Permit"
          type={rules.importPermit ? 'warning' : 'success'}
        />
        <StatCard 
          value={rules.microchipRequired ? 'Yes' : 'Check'} 
          label="Microchip Required"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-8 mb-12">
        {/* Left Column - Main Info */}
        <div className="lg:col-span-2 space-y-8">
          
          {/* Basic Requirements */}
          <section className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </span>
              Basic Requirements
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                {rules.microchipRequired ? (
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                )}
                <div>
                  <span className="font-medium text-slate-900">Microchip: </span>
                  <span className="text-slate-700">
                    {rules.microchipRequired ? 'Required - ISO 11784/11785 15-digit microchip' : 'Check specific requirements'}
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-medium text-slate-900">Rabies Vaccination: </span>
                  <span className="text-slate-700">
                    {rules.rabiesRequired ? `Required - ${rules.rabiesWaitingPeriod || '21 days'} minimum before travel` : 'Check requirements'}
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-medium text-slate-900">Health Certificate: </span>
                  <span className="text-slate-700">
                    {rules.healthCertificate ? `Required - valid for ${rules.healthCertificateValidity || '10 days'}` : 'Check specific requirements'}
                  </span>
                </div>
              </div>

              {rules.importPermit && (
                <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-medium text-amber-900">Import Permit: </span>
                    <span className="text-amber-800">
                      Required - Apply {rules.permitLeadTime || '2+ weeks'} before travel
                    </span>
                  </div>
                </div>
              )}

              {rules.bloodTest && (
                <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-medium text-red-900">Blood Test Required: </span>
                    <span className="text-red-800">{rules.bloodTest}</span>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Quarantine Information */}
          {rules.quarantine && (
            <section className="bg-red-50 border border-red-200 rounded-xl p-6">
              <h2 className="text-xl font-bold text-red-900 mb-4 flex items-center gap-2">
                <Clock className="w-6 h-6" />
                Quarantine Requirements
              </h2>
              
              <div className="space-y-3">
                <p className="text-red-800">
                  <strong>Duration:</strong> {rules.quarantineDays}
                </p>
                {rules.quarantineFacility && (
                  <p className="text-red-800">
                    <strong>Facility:</strong> {rules.quarantineFacility}
                  </p>
                )}
                <p className="text-red-700 text-sm mt-4">
                  Quarantine must be booked in advance. Contact the quarantine facility directly for reservations and current pricing.
                </p>
              </div>
            </section>
          )}

          {/* Special Requirements */}
          {country.specialRequirements && country.specialRequirements.length > 0 && (
            <section className="bg-white border border-slate-200 rounded-xl p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </span>
                Special Requirements for {country.name}
              </h2>
              
              <ul className="space-y-2">
                {country.specialRequirements.map((req, index) => (
                  <li key={index} className="flex items-start gap-2 text-slate-700">
                    <span className="text-amber-500 mt-1">•</span>
                    {req}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Banned Breeds */}
          {(country.bannedBreeds && country.bannedBreeds.length > 0) && (
            <section className="bg-white border border-slate-200 rounded-xl p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </span>
                Breed Restrictions
              </h2>
              
              <div className="flex flex-wrap gap-2">
                {country.bannedBreeds.map((breed, index) => (
                  <span key={index} className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm">
                    {breed}
                  </span>
                ))}
              </div>
              <p className="text-slate-600 text-sm mt-4">
                These breeds may be banned or require special permits/conditions for entry.
              </p>
            </section>
          )}

          {/* Common Issues */}
          {country.commonIssues && country.commonIssues.length > 0 && (
            <section className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h2 className="text-xl font-bold text-amber-900 mb-4">
                Common Issues to Avoid
              </h2>
              <ul className="space-y-2">
                {country.commonIssues.map((issue, index) => (
                  <li key={index} className="flex items-start gap-2 text-amber-800">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-1" />
                    {issue}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        {/* Right Column - Sidebar */}
        <div className="space-y-6">
          {/* CTA Card */}
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-6 text-white">
            <h3 className="text-xl font-bold mb-3">Flying to {country.name}?</h3>
            <p className="text-blue-100 text-sm mb-4">
              Verify your pet's crate and documents before you travel. Our AI pre-check ensures 
              you meet all {country.name} requirements.
            </p>
            <Link to="/">
              <Button className="w-full bg-white text-blue-600 hover:bg-blue-50">
                Check My Crate - $39
              </Button>
            </Link>
          </div>

          {/* Entry Points */}
          {country.entryPoints && (
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-600" />
                Approved Entry Points
              </h3>
              <p className="text-slate-700 text-sm">{country.entryPoints.join(', ')}</p>
            </div>
          )}

          {/* Preparation Timeline */}
          <div className="bg-slate-100 rounded-xl p-6">
            <h3 className="font-bold text-slate-900 mb-3">Preparation Timeline</h3>
            <div className="space-y-2 text-sm text-slate-700">
              {rules.quarantine || rules.bloodTest ? (
                <>
                  <p><strong>6+ months:</strong> Start preparation</p>
                  <p><strong>3-4 months:</strong> Blood tests, permits</p>
                  <p><strong>1 month:</strong> Final vaccinations</p>
                  <p><strong>10 days:</strong> Health certificate</p>
                </>
              ) : (
                <>
                  <p><strong>1-2 months:</strong> Check requirements</p>
                  <p><strong>3 weeks:</strong> Rabies vaccination</p>
                  <p><strong>10 days:</strong> Health certificate</p>
                  <p><strong>Travel day:</strong> All documents ready</p>
                </>
              )}
            </div>
          </div>

          {/* Documentation Required */}
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Documents Checklist
            </h3>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2 text-slate-700">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                Microchip documentation
              </li>
              <li className="flex items-start gap-2 text-slate-700">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                Rabies vaccination certificate
              </li>
              <li className="flex items-start gap-2 text-slate-700">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                Veterinary health certificate
              </li>
              {rules.importPermit && (
                <li className="flex items-start gap-2 text-slate-700">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  Import permit
                </li>
              )}
              {rules.bloodTest && (
                <li className="flex items-start gap-2 text-slate-700">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  Blood test results
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* SEO Content */}
      <section className="mt-12 pt-12 border-t border-slate-200">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-blue-600 mb-6">
            Importing Pets to {country.name}
          </h2>
          <div className="space-y-6 text-slate-700 leading-relaxed">
            <p className="text-lg">
              {country.name} has specific requirements for importing dogs and cats. Understanding these rules 
              before you travel is essential to avoid quarantine, fines, or having your pet denied entry.
            </p>
            
            <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl p-6 border border-slate-100">
              <h3 className="text-xl font-bold text-slate-900 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                Key Points for Pet Owners
              </h3>
              <p className="text-slate-600">
                {rules.quarantine 
                  ? `${country.name} is one of the stricter countries for pet imports, requiring mandatory quarantine. Plan your move at least 6 months in advance to meet all requirements.`
                  : `${country.name} allows relatively straightforward pet imports for travelers who prepare correctly. Ensure your rabies vaccination and health certificates are in order.`
                }
              </p>
            </div>

            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-100">
              <h3 className="text-xl font-bold text-amber-800 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                Avoiding Common Mistakes
              </h3>
              <p className="text-amber-700">
                The most common issues when importing pets to {country.name} include: expired health certificates 
                (must be within 10 days of travel), incorrect microchip format, and incomplete vaccination records. 
                Double-check all documentation before departure.
              </p>
            </div>
          </div>
        </div>
      </section>
    </SEOLayout>
  );
};

export default DestinationDetail;
